/**
 * Content Calendar — types shared between API and web.
 *
 * Posts live on a calendar. Each post belongs to a Campaign (a single
 * "generate" invocation). For every post the AI emits 6 variants:
 *   - 3 caption styles (short / long / bullets)
 *   - 3 visual styles (single image / multi-image / video)
 * The user picks one caption + one visual per post and can edit any of the
 * generated text inline. Posts can be left as drafts or assigned a
 * scheduled-for datetime.
 *
 * The AI behind the generator is fully isolated from chat agents and the
 * Support AI:
 *   - System prompt + knobs live in the ContentCalendarConfig singleton.
 *   - Knowledge base items use ownerKind='content_calendar' +
 *     CONTENT_CALENDAR_OWNER_ID (see knowledge-base.ts).
 */
import { JSON_OUTPUT_END, JSON_OUTPUT_START } from '../constants/markers.js';
export const CONTENT_PLATFORMS = [
    'facebook',
    'instagram',
    'x',
    'linkedin',
    'tiktok',
    'youtube',
    'website',
];
export const CONTENT_PERIOD_KINDS = ['single', 'week', 'month', 'custom'];
export const CONTENT_VARIANT_KINDS = [
    'caption_short',
    'caption_long',
    'caption_bullets',
    'visual_single',
    'visual_multi',
    'visual_video',
];
/**
 * Bulk generation from an uploaded Excel sheet.
 *
 * One spreadsheet row = one post, so every row is a `periodKind: 'single'`
 * campaign on its own start date. The cap exists because generation is
 * synchronous (10–60s per row) and nginx cuts a proxied request at 300s — the
 * browser sends one request PER ROW, and the cap bounds how long a user has to
 * stay on the page. It is enforced server-side, not just in the dialog.
 */
export const CONTENT_BULK_MAX_ROWS = 10;
/**
 * Largest row index a worksheet can have (the .xlsx format's own limit).
 *
 * Row numbers travel with a parsed row so an error can say "row 7" and match
 * what the user sees in Excel. That is the SHEET's row number, so it is NOT
 * bounded by CONTENT_BULK_MAX_ROWS — ten data rows already reach row 11, and
 * blank lines between entries push it further. Bounding it by the row cap
 * rejected the last row of every full sheet.
 */
export const EXCEL_MAX_ROW = 1_048_576;
/**
 * Byte cap for an uploaded sheet, shared by the calendar dialog and the chat
 * upload so the same file is accepted or rejected identically by both. A
 * ten-row workbook is a few KB; anything near this is not a filled template.
 */
export const CONTENT_BULK_MAX_SHEET_BYTES = 5 * 1024 * 1024;
/**
 * Human-facing platform names, matching the labels on the Create-content modal.
 *
 * These exist because the API values are lowercase slugs (`x`, `website`) while
 * the UI shows "X (Twitter)" and "Website / Blog". Someone filling a spreadsheet
 * types what they saw on screen, so the template gives each platform its own
 * yes/no column instead of a free-text cell — a value that can't be typed can't
 * be misspelled.
 */
export const CONTENT_PLATFORM_LABELS = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    x: 'X (Twitter)',
    linkedin: 'LinkedIn',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    website: 'Website / Blog',
};
/**
 * Cap for any single Text-On-Visual line, wherever it is typed or generated.
 * A TOV is a headline meant to sit on an image, not a caption — a longer one
 * cannot be read on the visual anyway.
 */
export const TOV_MAX_LENGTH = 300;
/** The two values every yes/no column accepts, and what the dropdown offers. */
export const CONTENT_BULK_BOOLEAN_VALUES = ['yes', 'no'];
/**
 * Template column headers, in order — mirroring the Create-content modal:
 * start date, platforms, topic, description, knowledge, hashtags, schedule.
 * "Period" has no column: one row is always one post.
 *
 * TOV is appended LAST rather than placed next to Topic as it is on screen.
 * Rows are read by column position, so inserting a column mid-sheet would
 * silently misread every template a user had already downloaded and filled.
 */
export const CONTENT_BULK_COLUMNS = [
    'Start date (YYYY-MM-DD)',
    ...CONTENT_PLATFORMS.map((p) => CONTENT_PLATFORM_LABELS[p]),
    'Topic',
    'Detailed description / objective',
    'Knowledge for this campaign (optional)',
    'Include hashtags',
    'Schedule on calendar',
    'TOV — Text On Visual (optional)',
];
/** Column indices (1-based, as ExcelJS numbers them). */
export const CONTENT_BULK_COL = {
    startDate: 1,
    /** Platform columns run from here, in CONTENT_PLATFORMS order. */
    firstPlatform: 2,
    topic: 2 + CONTENT_PLATFORMS.length,
    description: 3 + CONTENT_PLATFORMS.length,
    knowledgeNotes: 4 + CONTENT_PLATFORMS.length,
    includeHashtags: 5 + CONTENT_PLATFORMS.length,
    scheduled: 6 + CONTENT_PLATFORMS.length,
    tov: 7 + CONTENT_PLATFORMS.length,
};
// ---------------------------------------------------------------------------
// Agent dispatch mode 'content_calendar' — schema + prompt builders.
//
// The agent supports four actions (a discriminated union keyed on `action`):
//   - create           — collect campaign inputs + run generateCampaign()
//   - list             — list the user's recent campaigns
//   - delete_campaign  — delete a campaign by id (cascades to posts/variants)
//   - update_campaign  — change all posts in a campaign to drafts or scheduled
//
// Every prior workflow_result (campaign created / list returned / etc.) is
// surfaced back into the chat history so the agent can reference past
// campaign IDs when the user says "the one I just made" or "the second one".
// ---------------------------------------------------------------------------
const CREATE_BRANCH = {
    type: 'object',
    title: 'Create a new campaign',
    properties: {
        action: { const: 'create' },
        topic: {
            type: 'string',
            minLength: 1,
            maxLength: 200,
            description: 'Short headline of what this campaign is about.',
        },
        description: {
            type: 'string',
            minLength: 1,
            maxLength: 10_000,
            description: 'Detailed objective — angle, audience, call-to-action, proof points, anything the writer should know.',
        },
        platforms: {
            type: 'array',
            minItems: 1,
            maxItems: CONTENT_PLATFORMS.length,
            items: { type: 'string', enum: CONTENT_PLATFORMS },
        },
        includeHashtags: { type: 'boolean' },
        periodKind: { type: 'string', enum: CONTENT_PERIOD_KINDS },
        startDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' },
        endDate: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' },
        scheduled: { type: 'boolean' },
        knowledgeNotes: { type: 'string', maxLength: 20_000 },
    },
    required: [
        'action',
        'topic',
        'description',
        'platforms',
        'includeHashtags',
        'periodKind',
        'startDate',
        'scheduled',
    ],
    additionalProperties: false,
};
const LIST_BRANCH = {
    type: 'object',
    title: 'List recent campaigns',
    properties: {
        action: { const: 'list' },
        limit: { type: 'integer', minimum: 1, maximum: 50 },
    },
    required: ['action'],
    additionalProperties: false,
};
const DELETE_BRANCH = {
    type: 'object',
    title: 'Delete a campaign',
    properties: {
        action: { const: 'delete_campaign' },
        campaignId: {
            type: 'string',
            minLength: 8,
            description: 'The id of the campaign to delete. Take this from a prior list result or the campaign you just created.',
        },
    },
    required: ['action', 'campaignId'],
    additionalProperties: false,
};
const UPDATE_BRANCH = {
    type: 'object',
    title: 'Update a campaign',
    properties: {
        action: { const: 'update_campaign' },
        campaignId: {
            type: 'string',
            minLength: 8,
            description: 'The id of the campaign to modify.',
        },
        scheduled: {
            type: 'boolean',
            description: 'true → flip every post in the campaign to "scheduled". false → flip every post back to "draft".',
        },
    },
    required: ['action', 'campaignId', 'scheduled'],
    additionalProperties: false,
};
export function buildContentCalendarOutputSchema() {
    return {
        $schema: 'http://json-schema.org/draft-07/schema#',
        title: 'Content campaign action',
        description: 'Pick exactly one action: create | list | delete_campaign | update_campaign.',
        oneOf: [CREATE_BRANCH, LIST_BRANCH, DELETE_BRANCH, UPDATE_BRANCH],
    };
}
export function buildContentCalendarSystemPrompt() {
    const platformList = CONTENT_PLATFORMS.map((p) => {
        if (p === 'x')
            return 'X (Twitter)';
        if (p === 'tiktok')
            return 'TikTok';
        return p.charAt(0).toUpperCase() + p.slice(1);
    }).join(', ');
    return [
        'You are a content campaign assistant that drives the portal\'s Content Calendar.',
        '',
        'You have four actions, keyed on the `action` field of the proposal JSON:',
        '  1. `create`          — generate a new campaign from topic + description + period.',
        '  2. `list`            — list the user\'s recent campaigns (returns ids + topics so you can act on them next).',
        '  3. `delete_campaign` — permanently delete a specific campaign by id.',
        '  4. `update_campaign` — flip every post in a campaign between draft and scheduled.',
        '',
        '# Picking the right action',
        'Read the user\'s message and pick the action that fits. NEVER default to `create` if they\'re asking to delete, modify, or list. If they reference "that campaign" or "the one I just made", read the most recent `(System: …)` notes in the conversation — every campaign action emits one with the FULL campaign id.',
        '',
        'If you don\'t have a campaign id and need one, run `list` first — do not ask the user to type a UUID.',
        '',
        '# When to emit IMMEDIATELY (no follow-up questions)',
        'These actions have no fields to collect beyond what\'s already on the table — emit the JSON on the very next turn:',
        '- `list` whenever the user says "show", "list", "what do I have", "what campaigns are there".',
        '- `delete_campaign` once the user has confirmed which campaign to remove.',
        '- `update_campaign` once the user has confirmed which campaign + target status.',
        '',
        'Do NOT ask "how many do you want to see?" before listing — just use limit=10 unless the user already gave a number.',
        'Do NOT ask follow-up questions before listing. The list shows them everything they need to pick from.',
        '',
        '# Action: create — multi-turn data collection',
        'Collect these fields, ONE AT A TIME via natural conversation:',
        '- topic (required) — short headline.',
        '- description (required) — detailed objective: angle, audience, call-to-action, proof points.',
        `- platforms (required) — one or more of: ${platformList}.`,
        '- includeHashtags (required boolean) — default YES for Instagram/TikTok/X.',
        '- periodKind (required) — "single" (1 post), "week" (7 days), "month" (30 days), or "custom".',
        '- startDate (required YYYY-MM-DD) — convert "today"/"tomorrow" against the date in the "# Current date" section above.',
        '- endDate (YYYY-MM-DD) — required only when periodKind is "custom"; must be ≥ startDate.',
        '- scheduled (required boolean) — true puts posts straight on the calendar; false saves them as drafts.',
        '- knowledgeNotes (optional) — campaign-specific facts/quotes/stats/links.',
        'Recap before emitting and ask "Reply confirm to generate, or tell me what to change." On their confirm, emit the JSON.',
        '',
        '# Action: delete_campaign',
        'Before emitting: confirm the target out loud — "delete \\"<topic>\\" (id <short-id>…) — reply confirm to proceed?". On their confirm, emit `{ "action": "delete_campaign", "campaignId": "<FULL_UUID>" }`.',
        '',
        '# Action: update_campaign',
        'Before emitting: confirm the change out loud — "set every post in \\"<topic>\\" to scheduled (or draft) — reply confirm?". On their confirm, emit `{ "action": "update_campaign", "campaignId": "<FULL_UUID>", "scheduled": true|false }`.',
        '',
        '# Examples (follow these patterns exactly)',
        '',
        'Example 1 — list, one-shot:',
        'User: show my campaigns',
        'You: ' + JSON_OUTPUT_START + '\n{"action":"list","limit":10}\n' + JSON_OUTPUT_END,
        '',
        'Example 2 — list with custom limit:',
        'User: list my last 3 campaigns',
        'You: ' + JSON_OUTPUT_START + '\n{"action":"list","limit":3}\n' + JSON_OUTPUT_END,
        '',
        'Example 3 — delete with confirmation:',
        'User: delete the Odoo campaign',
        'You: Just to confirm — delete "How Odoo ERP Helps…" (id a70e5057…). Reply confirm to proceed.',
        'User: confirm',
        'You: ' + JSON_OUTPUT_START + '\n{"action":"delete_campaign","campaignId":"a70e5057-916d-424e-90a8-5ff250438717"}\n' + JSON_OUTPUT_END,
        '',
        'Example 4 — flip drafts to scheduled:',
        'User: schedule all posts of the Odoo campaign',
        'You: Got it — flip every post in "How Odoo ERP Helps…" to scheduled. Reply confirm?',
        'User: yes',
        'You: ' + JSON_OUTPUT_START + '\n{"action":"update_campaign","campaignId":"a70e5057-916d-424e-90a8-5ff250438717","scheduled":true}\n' + JSON_OUTPUT_END,
        '',
        'Example 5 — needs the list first:',
        'User: delete one of my campaigns',
        'You: ' + JSON_OUTPUT_START + '\n{"action":"list","limit":10}\n' + JSON_OUTPUT_END,
        '(the server replies with a list, then ask the user which one to delete)',
        '',
        '# After you emit the JSON',
        'The system handles dispatch and posts a result card into the chat. You don\'t need to do anything else — wait for the user\'s next message.',
    ].join('\n');
}
//# sourceMappingURL=content-calendar.js.map